import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import GavelIcon from "@mui/icons-material/Gavel";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SearchIcon from "@mui/icons-material/Search";
import {
  getAuthToken,
  getChargebackReport,
  getChargebackReportHtmlUrl,
  searchChargebackPayments,
} from "./reportApi";

const DEFAULT_SEARCH = "";

function formatCurrency(value) {
  return Number(value || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function formatPaymentSummary(item) {
  const count = Number(item.installmentCount || 1);
  if (count > 1) {
    return `${count} parcelas - ${count}x ${formatCurrency(item.installmentAmount)} - total ${formatCurrency(item.amount)}`;
  }

  return formatCurrency(item.amount);
}

function formatDate(value) {
  if (!value) return "N/A";
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value));
}

const ChargebackReport = () => {
  const [searchText, setSearchText] = useState(DEFAULT_SEARCH);
  const [selectedPaymentId, setSelectedPaymentId] = useState("");
  const [results, setResults] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadReportByPaymentId = async (paymentId) => {
    const normalized = paymentId.trim();
    if (!normalized) {
      setError("Informe um paymentId.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const data = await getChargebackReport(normalized);
      setSelectedPaymentId(normalized);
      setReport(data);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.details ||
          "Nao foi possivel carregar o relatorio."
      );
    } finally {
      setLoading(false);
    }
  };

  const loadReport = async () => {
    const normalized = searchText.trim();
    if (!normalized) {
      setError("Informe nome, e-mail ou paymentId.");
      return;
    }

    if (normalized.toLowerCase().startsWith("pay_")) {
      setResults([]);
      await loadReportByPaymentId(normalized);
      return;
    }

    setLoading(true);
    setError("");
    setReport(null);
    setSelectedPaymentId("");
    try {
      const data = await searchChargebackPayments(normalized);
      setResults(data.items || []);
      if (!data.items?.length) {
        setError("Nenhum pagamento encontrado para essa busca.");
      }
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.details ||
          "Nao foi possivel buscar pagamentos."
      );
    } finally {
      setLoading(false);
    }
  };

  const openHtml = () => {
    const normalized = selectedPaymentId || searchText.trim();
    if (!normalized) return;

    const token = getAuthToken();
    const url = getChargebackReportHtmlUrl(normalized);
    const html = `
      <html>
        <body>
          <p>Carregando relatorio...</p>
          <script>
            fetch(${JSON.stringify(url)}, {
              headers: { Authorization: "Bearer ${token}" }
            })
              .then((r) => r.text())
              .then((html) => {
                document.open();
                document.write(html);
                document.close();
              });
          </script>
        </body>
      </html>
    `;
    const newWindow = window.open("", "_blank");
    if (newWindow) {
      newWindow.document.write(html);
      newWindow.document.close();
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <GavelIcon color="primary" />
        <Box>
          <Typography variant="h5" fontWeight={800}>
            Defesa de chargeback
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gere a timeline e as evidencias para contestacao.
          </Typography>
        </Box>
      </Stack>

      <Paper sx={{ p: 2.5, mb: 2 }} variant="outlined">
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          <TextField
            label="Nome, e-mail ou payment ID"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            fullWidth
            size="small"
          />
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={loadReport}
            disabled={loading}
            sx={{ minWidth: 150 }}
          >
            Buscar
          </Button>
          <Button
            variant="outlined"
            startIcon={<OpenInNewIcon />}
            onClick={openHtml}
            disabled={!selectedPaymentId && !searchText.trim().toLowerCase().startsWith("pay_")}
            sx={{ minWidth: 160 }}
          >
            Abrir HTML
          </Button>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {results.length > 0 && (
        <Paper sx={{ p: 2.5, mb: 2 }} variant="outlined">
          <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1.5 }}>
            Pagamentos encontrados
          </Typography>
          <Stack spacing={1}>
            {results.map((item) => (
              <Box
                key={item.paymentId}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 2,
                  p: 1.5,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <Box>
                  <Typography fontWeight={800}>{item.fullName}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.email} - {item.installmentId || item.paymentId}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item.method} - {formatPaymentSummary(item)} - {formatDate(item.createdAt)}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setSearchText(item.fullName || item.email || item.paymentId);
                    loadReportByPaymentId(item.paymentId);
                  }}
                >
                  Gerar
                </Button>
              </Box>
            ))}
          </Stack>
        </Paper>
      )}

      {report && (
        <Paper sx={{ p: 2.5 }} variant="outlined">
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
            <Chip label={`${report.paymentIds.length} pagamento(s)`} />
            <Chip label={`${report.webhooks.length} webhook(s)`} />
            <Chip label={`${report.emails.length} evento(s) de e-mail`} />
            <Chip label={`${report.downloads.length} download(s)`} />
            <Chip label={`${report.timeline.length} item(ns) na timeline`} />
          </Stack>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Cliente
              </Typography>
              <Typography fontWeight={800}>{report.user.fullName}</Typography>
              <Typography variant="body2">{report.user.email}</Typography>
              <Typography variant="body2">{report.user.phone}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Compra
              </Typography>
              <Typography fontWeight={800}>
                {formatCurrency(report.purchase.totalGross)}
              </Typography>
              <Typography variant="body2">
                {report.purchase.method} - {report.purchase.financialStatus}
              </Typography>
              <Typography variant="body2">
                Cartao: {report.purchase.cardBrand || "N/A"} final{" "}
                {report.purchase.cardLast4 || "N/A"}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>
            Ultimos eventos
          </Typography>
          <Stack spacing={1}>
            {report.timeline.slice(-8).map((item, index) => (
              <Box key={`${item.at}-${index}`}>
                <Typography variant="body2" fontWeight={700}>
                  {formatDate(item.at)} - {item.event}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {item.source}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Paper>
      )}
    </Box>
  );
};

export default ChargebackReport;
